<template>
	<main class="home">
		<BrandBuilderToolbar
			:currentCampaign="routeCampaignId"
			:itemType="campaignType"
			:showPreview="showPreview"
			:class="{ smHideControlsToggle: smHideControls }"
		/>
		<aside
			v-if="showPreview"
			id="bb-nested-view"
			class="bb-nested-view"
			:class="{
				smHideControls: smHideControls,
				'modal-open': modalIsOpen,
			}"
		>
			<transition name="fade" mode="out-in">
				<router-view @modal-open="modalIsOpen = $event"></router-view>
			</transition>
		</aside>
		<div class="loading-icon-container" v-if="onPricing() && !showFlyer">
			<LoadingIcon />
			<p class="loading-message">
				We are loading your pricing flyer preview. This may take a moment.
			</p>
		</div>
		<ItemPreview :class="!showFlyer ? 'invisible' : ''" />
	</main>
</template>

<script>
import BrandBuilderToolbar from '@/components/brand-builder/BrandBuilderToolbar.vue';
import ItemPreview from '@/components/brand-builder/ItemPreview.vue';
import LoadingIcon from '@/components/global/LoadingIcon.vue';
import { mapActions, mapState, mapGetters } from 'vuex';
import {
	setCSSCustomFontColorProperties,
	setCSSCustomColorProperties,
} from '@/util/set-css-custom-properties';
import { CAMPAIGN_ID } from '@/constants/brand-builder';
import profileService from '@/services/profile/profile-service';

export default {
	name: 'CampaignEditorWorkspace',
	components: {
		BrandBuilderToolbar,
		ItemPreview,
		LoadingIcon,
	},
	computed: {
		...mapState({
			expandedMenu: (state) => state.menus.expandedMenu,
			campaignId: (state) => state.brandBuilderEditor.campaignId,
			selectedAudienceType: (state) =>
				state.brandBuilderEditor.selectedAudienceType,
			smHideControls: (state) => state.brandBuilderEditor.smHideControls,
			mediasByVariantId: (state) => state.brandBuilderEditor.mediasByVariantId,
			mediasByCampaignItemId: (state) =>
				state.brandBuilderEditor.mediasByCampaignItemId,
			palettes: (state) =>
				state.brandBuilderEditor.selectedImage
					? state.brandBuilderEditor.selectedImage.palettes
					: null,
			brandFont: (state) =>
				(state.profile.brand[0] && state.profile.brand[0].value) || '',
			selectedColorIndex: (state) =>
				state.brandBuilderEditor.selectedColorIndex,
			profile: (state) => state.profile,
			companyProfile: (state) => state.companyProfile,
			brandingOptions: (state) => {
				if (!state.profile.blinkRegistrationInd) {
					state.workspaceBranding.brandingOptions[11].selected = false;
				}
				return state.workspaceBranding.brandingOptions;
			},
		}),
		...mapGetters(['userColorPalette', 'companyColorPalette']),
	},
	data() {
		return {
			showPreview: true,
			campaignType: null,
			routeCampaignId: this.$route.params.campaignId,
			modalIsOpen: false,
			showFlyer: !this.$route.fullPath.match(/pricing/g),
		};
	},
	watch: {
		selectedColorIndex(newIndex) {
			if (newIndex !== null) {
				if (this.selectedColorIndex === 1) {
					// User Colors
					setCSSCustomColorProperties(this.userColorPalette);
				} else if (this.selectedColorIndex === 2) {
					// Company Colors
					setCSSCustomColorProperties(this.companyColorPalette);
				} else if (this.palettes) {
					setCSSCustomColorProperties(
						this.palettes[this.selectedColorIndex - 3],
					);
				}
			}
		},
		mediasByVariantId(newMedias) {
			if (newMedias) {
				/* When mediasByVariantId is set on state then we must have  all the image objects (medias).
					A selectedImage object will also be set by default.  So use that object's color palette
					to set CSS variables that are used by ItemPreview component. */
				if (this.brandFont && this.palettes) {
					setCSSCustomFontColorProperties(this.brandFont, this.palettes[0]);
				}
			}
		},
	},
	methods: {
		...mapActions([
			'toggleExpandedMenu',
			'selectMediaType',
			'selectAudienceType',
			'brandBuilderDestroyed',
			'brandBuilderCreated',
			'loadCampaign',
			'updateDisclaimers',
			'resetDefaultSelections',
			'resetBrandBuilderState',
			'resetPricingFlyerData',
			'updateControlValue',
			'brandingOptionsToggle',
			'updatePricingText',
			'updateProfile',
			'brandingOptionsDisable',
			'setClientConnectAlertEmailDetails',
		]),
		onPricing() {
			return this.$route.fullPath.match(/pricing/g);
		},
	},
	created() {
		this.brandBuilderCreated();
	},
	mounted() {
		// Disable "Display Company Logo" if no company or personal company logo is loaded
		const noPersonalLogo =
			this.profile.personalCompanyLogo === null ||
			this.profile.personalCompanyLogo.imageUri === '';
		const noCompanyLogo =
			this.companyProfile.companyLogo === null ||
			this.companyProfile.companyLogo.imageUri === '';
		if (noPersonalLogo && noCompanyLogo) {
			this.brandingOptionsDisable({
				option: 'Display Company Logo',
				disabled: true,
			});
		} else {
			this.brandingOptionsDisable({
				option: 'Display Company Logo',
				disabled: false,
			});
		}

		// Disable "Display Profile Image" if no profile image is loaded
		if (
			this.profile.profilePhoto === null ||
			this.profile.profilePhoto.imageUri === ''
		) {
			this.brandingOptionsDisable({
				option: 'Display Profile Image',
				disabled: true,
			});
		} else {
			this.brandingOptionsDisable({
				option: 'Display Profile Image',
				disabled: false,
			});
		}

		// Disable "Display QR Code" if no QR code is loaded
		if (
			this.profile.blinkQRCode === null ||
			this.profile.blinkQRCode.imageUri === ''
		) {
			this.brandingOptionsDisable({
				option: 'Display QR Code',
				disabled: true,
			});
		} else {
			this.brandingOptionsDisable({
				option: 'Display QR Code',
				disabled: false,
			});
		}

		if (
			this.routeCampaignId === this.campaignId &&
			this.mediasByVariantId !== null
		) {
			/* The campaign we are viewing has already been loaded. */
			/* Set default CSS variables that are used by the ItemPreview component. */
			if (this.selectedColorIndex) {
				if (this.selectedColorIndex === 1) {
					// User Colors
					setCSSCustomFontColorProperties(
						this.brandFont,
						this.userColorPalette,
					);
				} else if (this.selectedColorIndex === 2) {
					// Company Colors
					setCSSCustomFontColorProperties(
						this.brandFont,
						this.companyColorPalette,
					);
				} else {
					setCSSCustomFontColorProperties(
						this.brandFont,
						this.palettes[this.selectedColorIndex - 3],
					);
				}
			} else {
				setCSSCustomFontColorProperties(this.brandFont, this.palettes[0]);
			}
			if (this.campaignId === CAMPAIGN_ID.PRICING_SCENARIO_FLYER) {
				this.showFlyer = true;
			}
		} else {
			/* User is routing a differet campaign than what we have in state
			   or we don't have the whole campaign. */
			if (this.$route.query.alertId) {
				this.resetDefaultSelections({ clearMediaType: false });
				/* for client connect alerts */
				this.setClientConnectAlertEmailDetails({
					alertId: this.$route.query.alertId,
					borrowerFirstName: this.$route.query.borrowerFirstName,
					borrowerLastName: this.$route.query.borrowerLastName,
					email: this.$route.query.email,
					leadId: this.$route.query.leadId,
					alertType: this.$route.query.alertType,
				});
			} else {
				this.resetBrandBuilderState();
			}
			this.loadCampaign(this.$route.params.campaignId).finally(() => {
				/* TODO: Refactor this to watch for the font being loaded
				(there is an npm library to do this) and set using a promise */
				// TODO: refactor to eliminate eslint error
				// eslint-disable-next-line no-return-assign
				setTimeout(() => (this.showFlyer = true), 1000);
			});

			if (this.routeCampaignId === CAMPAIGN_ID.PRICING_SCENARIO_FLYER) {
				// Set-up state for pricing flyer, which is a special case
				this.selectMediaType({ index: 6 });
				this.selectAudienceType({ index: 1 });
				// put user on pricing tab if they aren't already
				if (!this.onPricing()) {
					this.$router.push({
						path: `/campaign-editor-workspace/${this.routeCampaignId}/pricing`,
					});
				}
			} else {
				// put user on media tab if they aren't already
				const onMedia = this.$route.fullPath.match(/media/g);
				if (!onMedia) {
					this.$router.push({
						path: `/campaign-editor-workspace/${this.routeCampaignId}/media`,
					});
				}
			}
		}

		if (this.expandedMenu) {
			this.toggleExpandedMenu();
		}

		if (this.profile.personalCompanyLogo !== null) {
			if (
				this.profile.personalCompanyLogo.imageUri === '' &&
				this.companyProfile.companyLogo === null &&
				this.brandingOptions[0].selected === true
			) {
				this.brandingOptionsToggle(this.brandingOptions[0]);
			} else if (
				this.profile.personalCompanyLogo.imageUri === '' &&
				this.companyProfile.companyLogo.imageUri === '' &&
				this.brandingOptions[0].selected === true
			) {
				this.brandingOptionsToggle(this.brandingOptions[0]);
			}
		} else {
			// TODO: refactor to eliminate eslint error
			// eslint-disable-next-line no-lonely-if
			if (
				this.companyProfile.companyLogo === null &&
				this.brandingOptions[0].selected === true
			) {
				this.brandingOptionsToggle(this.brandingOptions[0]);
			} else if (
				this.companyProfile.companyLogo.imageUri === '' &&
				this.brandingOptions[0].selected === true
			) {
				this.brandingOptionsToggle(this.brandingOptions[0]);
			}
		}
	},
	beforeDestroy() {
		if (!this.expandedMenu) {
			this.toggleExpandedMenu();
		}
	},
	destroyed() {
		if (this.$route.matched[0].path !== '/campaign-editor-workspace') {
			this.brandBuilderDestroyed();
			this.resetBrandBuilderState();
		}
		if (this.routeCampaignId === CAMPAIGN_ID.PRICING_SCENARIO_FLYER) {
			this.resetPricingFlyerData();
		}
	},
};
</script>

<style scoped lang="scss">
.view {
	margin-top: $header-height;
	background-color: $off-white;
}

.home {
	position: relative;
	background-color: $off-white;
	height: calc(
		100vh - ((#{$header-height} + 1px) + #{$brand-builder-mobile-footer})
	); /* This accounts for the Brand Builder viewport height on mobile by subtracting the Header and footer height from the viewport height. */
	@include larger-than-mobile {
		height: calc(
			100vh - ((#{$header-height} + 1px) + 59px)
		); /* This accounts for the Brand Builder viewport height on tablet by subtracting the Header and footer height from the viewport height. */
	}

	@media only screen and (min-width: 1375px) {
		height: calc(
			100vh - ((#{$header-height} + 1px) + 44px)
		); /* This accounts for the Brand Builder viewport height on desktop by subtracting the Header and footer height from the viewport height. */
	}
}

.loading-icon-container {
	display: flex;
	justify-content: center;
	margin-left: calc(#{$brand-builder-sub-view-with} + (#{$base-gap} * 2));
	flex-direction: column;
	align-items: center;
	overflow: hidden;
	overflow-y: auto;
	position: absolute;
	top: 0;
	right: 0;
	bottom: 0;
	left: 0;

	@include sm {
		height: calc(100% - #{$brand-builder-tool-height});
	}

	@media screen and (max-height: 630px) {
		overflow-y: scroll;
	}

	@include sm {
		margin-left: 0;
	}

	@media only screen and (max-width: $mobile-max) {
		margin-left: 0;
	}

	@include mobile {
		display: block;
		@include rem(margin-left, 15px);
		@include rem(margin-right, 15px);

		/* HTML emailers styles */
		> table {
			margin-bottom: 120px; /* allows the content to show above the Brand Builder Nav. */
		}

		.footer-wrapper p {
			word-break: break-word;
		}
	}
}

.invisible {
	opacity: 0;
}

.temp {
	position: absolute;
	left: 30%;
	right: 30%;
	width: 40%;
	bottom: 0;
}

.smHideControls {
	@include sm {
		display: none;
	}
}

.bb-nested-view {
	width: $brand-builder-sub-view-with;
	position: fixed;
	top: $top-nav-height + 1px;
	left: $brand-builder-width;
	background-color: $white;
	border-left: 1px solid $border-color-light;
	border-right: 1px solid $border-color-light;
	height: calc(100vh - (#{$top-nav-height} + (#{$base-gap} * 2)));
	z-index: 2; //was 1
	padding: $base-gap;
	-ms-overflow-style: none;
	overflow: -moz-scrollbars-none;
	scrollbar-width: none;
	@include lg {
		overflow-x: visible;
	}
	overflow-y: auto;

	&::-webkit-scrollbar {
		@include rem(width, 10px);
	}
	&::-webkit-scrollbar-thumb {
		background: transparent;
	}

	@media only screen and (min-width: ($breakpoint+1px)) {
		/* This is a bit of slight of hand to help Safari 13 handle the
        modal on large screens with nested positioned fixed elements*/
		&.modal-open {
			width: $brand-builder-sub-view-with;
			background: none;
		}
	}

	&:hover:not(.modal-open) {
		@include rem(scrollbar-width, 10px);
		scrollbar-color: $brand-teal;

		&::-webkit-scrollbar-thumb {
			background: $brand-teal;
		}
	}

	@include sm {
		box-sizing: border-box;
		top: $top-nav-height + 1px;
		height: calc(
			100vh - (#{$top-nav-height} + 1px + #{$brand-builder-tool-height} + 59px)
		); //211px
		left: 0;
		width: 100%;
		z-index: 3;
	}

	@include mobile {
		left: 0;
		height: calc(
			100vh -
				(
					#{$top-nav-height + 1 + $brand-builder-tool-height +
						$brand-builder-mobile-footer}
				)
		); //248px
		padding: $base-gap-mobile $base-gap-mobile $base-gap-mobile;
	}
}

.fade-enter-active,
.fade-leave-active {
	transition: opacity $b360-motion-duration--shortest
		$b360-motion-easing--standard;
}

.fade-enter,
.fade-leave-active {
	opacity: 0;
}
</style>
